import React from 'react'
import {useIntl, defineMessage} from 'react-intl'
import PropTypes from 'prop-types'
import {Box, IconButton} from '@chakra-ui/react'
import {ChevronRightIcon, ChevronLeftIcon} from '../../components/custom-icons/index.jsx'

/**
 * ArrowButton is a functional React component that renders a customizable button with an arrow icon.
 * It is typically used for navigation purposes, such as in a carousel, to move to the next or previous item.
 *
 * @param {Object} props - The properties object.
 * @param {'next'|'prev'} props.direction - The direction of the arrow, either 'next' or 'prev'.
 * @param {Function} props.onClick - The callback function to be executed when the button is clicked.
 * @param {string} [props.className] - Optional additional CSS class names to apply to the button.
 * @param {Object} [props.style] - Optional inline styles to apply to the button.
 * @param {boolean} [props.disabled] - Optional flag to disable the button, preventing user interaction.
 *
 * @returns {JSX.Element} A button component with an arrow icon, styled and configured based on the provided props.
 *
 * @example
 * <ArrowButton
 *   direction="next"
 *   onClick={handleNext}
 *   className="custom-class"
 *   style={{ margin: '10px' }}
 *   disabled={false}
 * />
 */
export const ArrowButton = ({direction, onClick, className, style, disabled}) => {
    const intl = useIntl()
    const messages = {
        carouselButtonNext: defineMessage({
            id: 'carousel.button.next',
            defaultMessage: 'Next'
        }),
        carouselButtonPrev: defineMessage({
            id: 'carousel.button.prev',
            defaultMessage: 'Previous'
        })
    }
    const message = direction === 'next' ? messages.carouselButtonNext : messages.carouselButtonPrev

    return (
        <Box style={{...style}}>
            <IconButton
                aria-label={intl.formatMessage(message)}
                icon={
                    direction === 'next' ? (
                        <ChevronRightIcon boxSize="12px" />
                    ) : (
                        <ChevronLeftIcon boxSize="12px" />
                    )
                }
                variant="ghost"
                onClick={onClick}
                isDisabled={disabled}
                className={className}
                w={'40px'}
                h={'40px'}
                border={'1px solid var(--chakra-colors-gray)'}
            />
        </Box>
    )
}

ArrowButton.propTypes = {
    direction: PropTypes.oneOf(['next', 'prev']).isRequired,
    className: PropTypes.string,
    style: PropTypes.object,
    onClick: PropTypes.func,
    disabled: PropTypes.bool
}
